// DailyImproveModal — "What's New" pop-up for the 10am daily self-improve run (F4).
// Shows once per run that produced news (quality merges, feature PRs, or a revert). Native
// <dialog> + framer-motion (mirrors AppearanceView). Dismiss marks the run seen.

import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, GitMerge, GitPullRequest, Undo2, Boxes } from 'lucide-react';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };

const KIND_META = {
  quality: { Icon: GitMerge, label: 'Mergeado', cls: 'text-apolo-success' },
  feature: { Icon: GitPullRequest, label: 'PR aberto', cls: 'text-apolo-accent' },
  models: { Icon: Boxes, label: 'Modelos LLM', cls: 'text-apolo-accent' },
  revert: { Icon: Undo2, label: 'Revertido', cls: 'text-apolo-warning' },
};

function Entry({ entry }) {
  const meta = KIND_META[entry.kind] || KIND_META.feature;
  return (
    <li className="flex items-start gap-3 border-b border-apolo-borderSubtle py-3 last:border-b-0">
      <meta.Icon size={15} className={`mt-0.5 shrink-0 ${meta.cls}`} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm text-apolo-textPrimary">{entry.summary}</span>
        <span className="flex items-center gap-2 text-[11px] text-apolo-textTertiary">
          <span className={meta.cls}>{meta.label}</span>
          {entry.link ? <span className="truncate font-mono">{entry.link}</span> : null}
        </span>
      </div>
    </li>
  );
}

export default function DailyImproveModal({ open, report, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.showModal();
    if (!open && ref.current && ref.current.open) ref.current.close();
  }, [open]);

  const changelog = Array.isArray(report?.changelog) ? report.changelog : [];

  return (
    <dialog
      ref={ref}
      aria-labelledby="daily-improve-title"
      onClose={onClose}
      className="rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary shadow-2xl backdrop:bg-apolo-overlay backdrop:backdrop-blur-sm"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ scale: 0.97, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0, y: 8 }}
            transition={SPRING}
            className="flex w-[min(30rem,100vw)] flex-col"
          >
            <header className="flex items-center justify-between border-b border-apolo-borderSubtle p-4">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="shrink-0 text-apolo-accent" aria-hidden="true" />
                <h2 id="daily-improve-title" className="text-base font-semibold">Apolo melhorou hoje</h2>
              </div>
              <button
                type="button"
                aria-label="Fechar"
                onClick={onClose}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary transition hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accentRing"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </header>
            <div className="px-4 py-2">
              <p className="py-2 text-xs text-apolo-textTertiary">
                A auto-melhoria diária das 10h rodou. Mudanças de qualidade já entraram + deployaram;
                features ficam como PR pra você revisar.
              </p>
              <ul className="flex flex-col">
                {changelog.length === 0
                  ? <li className="py-3 text-sm text-apolo-textTertiary">Sem novidades hoje.</li>
                  : changelog.map((e, i) => <Entry key={i} entry={e} />)}
              </ul>
            </div>
            <footer className="flex justify-end border-t border-apolo-borderSubtle p-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
              >
                Entendi
              </button>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </dialog>
  );
}
